<template>
  <div
    class="flex items-center flex-grow-0 w-full border-b border-slate-400"
    :class="{
      'bg-pure-white': !isAdded,
      'bg-slate-200': isAdded
    }"
  >
    <div
      class="flex justify-between flex-shrink-0 gap-2 px-4 py-3 overflow-hidden border-r h-22 border-slate-400"
      :style="{ width: '20%' }"
      :title="target.name"
    >
      <div class="flex gap-2">
        <BlInputCheck
          v-if="useCheckbox"
          :checked="isChecked"
          :disabled="isAdded"
          @changed="$emit('check')"
          class="mt-1"
        />
        <div @click="$emit('select')">
          <BlTextBody1
            class="cursor-pointer text-slate-1000 line-clamp-2 h-13 hover:underline"
            :style="{
              textDecoration: 'underline',
              textDecorationColor: '#D0D5DD'
            }"
            :text="target.name"
          />
        </div>
      </div>
      <BlInputButton
        v-if="useAddButton"
        type="outlined"
        dense
        :disabled="isAdded"
        :label="isAdded ? 'Added' : 'Add'"
        @clicked="$emit('add')"
      />
    </div>
    <div
      v-if="useCurriculum"
      class="flex flex-shrink-0 gap-2 px-4 py-3 h-22"
      :title="target.curriculum_name"
      :style="{ width: '15%' }"
    >
      <div
        class="h-4 w-4 flex-shrink-0 rounded-full mt-0.5"
        :style="{ backgroundColor: target.curriculum_color }"
      />
      <BlTextBody1 class="line-clamp-2 h-13" :text="target.curriculum_name" />
    </div>
    <div
      class="flex-shrink-0 px-4 py-3 h-22"
      :style="{ width: descriptionWidth }"
      :title="target.description"
    >
      <BlTextBody1 class="whitespace-pre-line line-clamp-2 h-13" :text="target.description" />
    </div>
    <div class="flex-shrink-0 px-4 py-3 h-22" :style="{ width: '15%' }">
      <BlTextBody1 :text="targetTypes[target.type]" />
    </div>
    <div
      class="flex-shrink-0 px-4 py-3 space-y-2 h-22"
      :style="{ width: successMetricWidth }"
    >
      <div
        class="flex items-start gap-2"
        :title="generateSuccessMetric(target)"
      >
        <BlIcon icon="goal" color="slate-6" class="w-5 mt-0.5" />
        <BlTextBody1
          class="line-clamp-2"
          :text="generateSuccessMetric(target)"
        />
      </div>
      <div v-if="target.type.includes('Percentage')" class="flex gap-2">
        <BlIcon icon="tick-square" color="slate-6" class="w-5" />
        <BlTextBody1 :text="`${target.number_of_trial} trial(s)`" />
      </div>
      <div v-if="target.type.includes('Pir')" class="flex gap-2">
        <BlIcon icon="timer" color="slate-6" class="w-5" />
        <BlTextBody1 :text="`${target.duration} minute(s)`" />
      </div>
    </div>
    <div
      v-if="useAction"
      class="flex justify-end flex-shrink-0 gap-2 px-4 py-3 h-22"
      :style="{ width: '10%' }"
    >
      <BlInputButton
        label="Duplicate"
        type="outlined"
        dense
        @clicked="duplicateOpened = true"
      />
      <BlInputButton
        label="Edit"
        type="outlined"
        dense
        @clicked="editOpened = true"
      />
    </div>

    <BlBackdrop
      v-if="duplicateOpened"
      title="Duplicate the target"
      label="Duplicate the target"
      type="success"
      :disabled="submitLoading"
      @backdrop-close="duplicateOpened = false"
      @backdrop-action="onDuplicate"

    >
      <div class="px-8" :style="{width: '70ch'}">
        <BlTextBody2 text="You are about to duplicate the target" />
        <BlTextBody2 class="font-bold" :text="`${target.name}`" />
        <BlTextBody2 text="to the databank." />
        <BlTextBody2 text="Are you sure you want to proceed?" />
      </div>
    </BlBackdrop>

    <SuperAdminDatabankEditModal
      v-if="editOpened"
      :targetId="target.id"
      :disabledUpdateCurriculum="disabledUpdateCurriculum"
      @close-modal="editOpened = false"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";

import SuperAdminDatabankEditModal from "./EditDatabankModal.vue";

import BlIcon from "../../BlIcon.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputCheck from "../../BlInputCheck.vue";
import BlBackdrop from "../../BlBackdrop.vue";
import BlTextBody2 from "../../BlTextBody2.vue";

export default {
  name: "SuperAdminDatabankItem",
  components: {
    SuperAdminDatabankEditModal,
    BlTextBody1,
    BlIcon,
    BlInputButton,
    BlBackdrop,
    BlInputCheck,
    BlTextBody2
  },
  props: {
    panelId: { type: String, required: false, default: "panel-id" },
    target: { type: Object, required: true },
    useCurriculum: { type: Boolean, required: false, default: false },
    useAction: { type: Boolean, required: false, default: false },
    useAddButton: { type: Boolean, required: false, default: false },
    useCheckbox: { type: Boolean, required: false, default: false },
    isAdded: { type: Boolean, required: false, default: false },
    isChecked: { type: Boolean, required: false, default: false },
    disabledUpdateCurriculum: { type: Boolean, required: false, default: false }
    // @select
    // @add
    // @check
  },
  data() {
    return {
      duplicateOpened: false,
      editOpened: false,
      submitLoading: false,
      targetTypes: {
        "Target::Percentage": "Percentage",
        "Target::Pir": "Partial interval recording",
        "Target::Duration": "Duration",
        "Target::Frequency": "Frequency",
        "Target::Prompting": "Prompting"
      }
    };
  },
  computed: {
    descriptionWidth() {
      if (!this.useCurriculum) return "40%";
      return "25%";
    },
    successMetricWidth() {
      if (!this.useAction) return "25%";
      return "15%";
    }
  },
  methods: {
    ...mapActions(["deleteTarget", "duplicateTarget", "showToast"]),
    generateSuccessMetric(target) {
      let prefix = "";
      let goalText = "";
      let suffix = "";
      // prefix
      if (target.success_metric === "equal to or greater than goal") {
        prefix = "≥ ";
      }
      if (target.success_metric === "less than goal") {
        prefix = "< ";
      }
      // goal text
      if (target.type.includes("Duration")) {
        goalText = target.goal_time;
      } else if (target.type.includes("Prompting")) {
        goalText = `${target.success_metric} prompt min. ${target.goal}`;
      } else {
        goalText = target.goal;
      }
      // suffix
      if (target.type.includes("Percentage") || target.type.includes("Pir")) {
        suffix = "%";
      }
      if (
        target.type.includes("Frequency") ||
        target.type.includes("Prompting")
      ) {
        suffix = " attempt(s) per session";
      }
      return prefix + goalText + suffix;
    },

    async onDuplicate() {
      this.submitLoading = true;
      const params = { id: this.target.id, targetable_id: this.$route.params.id, targetable_type: "Databank", kind: "databank" };
      const {success, data} = await this.duplicateTarget(params);
      if (!success) return this.showToast({ message: "Something went wrong", type: "error" });
      this.showToast({ message: `Target ${data.name} duplicated successfully`, type: "success" });
      this.submitLoading = false;
      this.duplicateOpened = false;
    }

  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/databank/Item.vue